/**
 * Plugin Screen-Context Bridge
 *
 * Lets *any* plugin volunteer "what's on screen" to the assistant overlay
 * through the event API it already has — no assistant-specific plugin API,
 * no manifest changes. A plugin publishes:
 *
 *   ctx.events.publish('screen-context', { summary: '...', data: {...} })
 *
 * from wherever it already tracks its own page state (see
 * pages/workflows/workspace/WorkflowsWorkspace.tsx for the in-repo pattern of re-publishing whenever
 * the relevant state changes). This module subscribes once, with
 * `sourcePluginId: '*'`, and mirrors whatever it receives into
 * screen-context-registry as a per-plugin contributor.
 *
 * This one subscription covers both plugin rendering paths:
 *   - Directly-rendered React component plugins publish straight into
 *     the unified `usePluginEventBusStore` (host-ui-api.ts).
 *   - Iframe-rendered plugin widgets publish through PluginBridge's
 *     `events.publish` RPC, which also forwards into the same unified bus
 *     (see PluginBridge.ts).
 *
 * Contributions are static snapshots, not live like the core-page
 * contributors in screen-context-registry.ts's own doc — a plugin must
 * re-publish when its state changes to keep its contribution current.
 * They only count on the publishing plugin's own pages (see
 * buildScreenContextPayload), so a stale one can't follow the user around.
 */
import { usePluginEventBusStore } from '../../plugins/plugin-event-bus';
import { useScreenContextRegistry } from '../../plugins/registries/screen-context-registry';

const SUBSCRIBER_ID = 'core-assistant';
const EVENT_NAME = 'screen-context';

interface ScreenContextEventPayload {
  summary?: string;
  data?: Record<string, unknown>;
  /** Page id pattern(s) this contribution applies to; omit to apply wherever this plugin is on screen. */
  pageIds?: string[];
}

/**
 * Start listening for `screen-context` events from any plugin. Returns an
 * unsubscribe function. Safe to call once at app root (AssistantOverlayHost).
 */
export function startPluginScreenContextBridge(): () => void {
  return usePluginEventBusStore.getState().on(SUBSCRIBER_ID, '*', EVENT_NAME, (event) => {
    const payload = event.payload as ScreenContextEventPayload | undefined;
    if (!payload?.summary) return;
    useScreenContextRegistry.getState().register({
      id: `plugin.${event.sourcePluginId}.screen-context`,
      pluginId: event.sourcePluginId,
      pageIds: payload.pageIds,
      getContext: () => ({ summary: payload.summary!, data: payload.data }),
    });
  });
}
